// components/ErrorBoundary/index.jsx
import React from 'react';

const API_BASE = process.env.REACT_APP_API_APP || '';

/**
 * Envía el error al backend para logging centralizado.
 * Fire-and-forget: no bloquea el render si falla.
 */
const sendErrorToBackend = (errorData) => {
    try {
        // Intentar recuperar user info del localStorage/sessionStorage
        let userId = null, userEmail = null, userRole = null;
        try {
            // AWS Amplify guarda tokens en localStorage con un patrón específico
            const keys = Object.keys(localStorage);
            const accessTokenKey = keys.find(k => k.includes('accessToken'));
            const idTokenKey = keys.find(k => k.includes('idToken'));
            if (idTokenKey) {
                const idToken = localStorage.getItem(idTokenKey);
                const payload = JSON.parse(atob(idToken.split('.')[1]));
                userId = payload?.sub || null;
                userEmail = payload?.email || null;
                userRole = payload?.['custom:role'] || null;
            }
        } catch (_) { /* ignore */ }

        const body = {
            ...errorData,
            userId,
            userEmail,
            userRole,
            viewport: `${window.innerWidth}x${window.innerHeight}`,
            online: navigator.onLine,
            language: navigator.language,
        };

        // Usar keepalive para que el request llegue aunque el usuario cierre la pestaña
        fetch(`${API_BASE}/client-errors`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
            keepalive: true,
        }).catch(() => { /* silencioso - no bloquear si el backend está caído */ });
    } catch (_) { /* ignore errors in error logger */ }
};

/**
 * ErrorBoundary global: captura errores de React en el árbol de componentes
 * y muestra UI de fallback en lugar de pantalla en blanco.
 *
 * Logea errores en consola + sessionStorage + envía al backend para
 * debugging centralizado (tabla DynamoDB client_errors_prod).
 */
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
            errorId: null,
        };
    }

    static getDerivedStateFromError(error) {
        // Generar un ID único para que el usuario lo pueda reportar
        const errorId = `ERR-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
        return { hasError: true, error, errorId };
    }

    componentDidCatch(error, errorInfo) {
        // Log estructurado para CloudWatch / consola del browser
        const errorData = {
            errorId: this.state.errorId,
            errorName: error?.name,
            message: error?.message,
            stack: error?.stack,
            componentStack: errorInfo?.componentStack,
            url: window.location.href,
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString(),
            errorType: 'boundary',
        };

        // Enviar al backend (fire-and-forget)
        sendErrorToBackend(errorData);

        console.error('═══════════════════════════════════════════');
        console.error('🚨 ERROR CAPTURADO POR ErrorBoundary');
        console.error('═══════════════════════════════════════════');
        console.error('ID:', this.state.errorId);
        console.error('Mensaje:', error?.message);
        console.error('Stack:', error?.stack);
        console.error('Component Stack:', errorInfo?.componentStack);
        console.error('URL:', window.location.href);
        console.error('═══════════════════════════════════════════');

        // Guardar errorInfo en estado para debugging
        this.setState({ errorInfo });

        // Guardar en sessionStorage para recuperación rápida
        try {
            sessionStorage.setItem('lastAppError', JSON.stringify(errorData));
        } catch (_) { /* ignore */ }

        // TODO: enviar a servicio de logging externo si se quiere
        // fetch('/api/log-client-error', { method: 'POST', body: JSON.stringify(errorData) });
    }

    handleReload = () => {
        // Limpiar cache y recargar
        try {
            sessionStorage.clear();
            localStorage.removeItem('waiting-room-session');
        } catch (_) { /* ignore */ }
        window.location.href = '/';
    };

    handleSoftRetry = () => {
        this.setState({ hasError: false, error: null, errorInfo: null, errorId: null });
    };

    render() {
        if (this.state.hasError) {
            return (
                <div style={styles.container}>
                    <div style={styles.card}>
                        <div style={styles.iconWrapper}>
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={styles.icon}>
                                <circle cx="12" cy="12" r="10"/>
                                <line x1="12" y1="8" x2="12" y2="12"/>
                                <line x1="12" y1="16" x2="12.01" y2="16"/>
                            </svg>
                        </div>

                        <h1 style={styles.title}>Algo salió mal</h1>
                        <p style={styles.subtitle}>
                            Ocurrió un error inesperado. Nuestro equipo ha sido notificado.
                            Por favor, recarga la página para continuar.
                        </p>

                        {this.state.errorId && (
                            <div style={styles.errorIdBox}>
                                <span style={styles.errorIdLabel}>Código de error:</span>
                                <code style={styles.errorIdCode}>{this.state.errorId}</code>
                            </div>
                        )}

                        <div style={styles.buttons}>
                            <button onClick={this.handleReload} style={styles.btnPrimary}>
                                Recargar página
                            </button>
                            <button onClick={this.handleSoftRetry} style={styles.btnSecondary}>
                                Intentar de nuevo
                            </button>
                        </div>

                        {/* Detalles técnicos (solo en dev o si hay parámetro ?debug=1) */}
                        {(process.env.NODE_ENV === 'development' || window.location.search.includes('debug=1')) && this.state.error && (
                            <details style={styles.details}>
                                <summary style={styles.detailsSummary}>Detalles técnicos</summary>
                                <div style={styles.detailsContent}>
                                    <p style={styles.detailsLabel}>Mensaje:</p>
                                    <pre style={styles.detailsPre}>{this.state.error.message}</pre>
                                    <p style={styles.detailsLabel}>Stack:</p>
                                    <pre style={styles.detailsPre}>{this.state.error.stack}</pre>
                                    {this.state.errorInfo && (
                                        <>
                                            <p style={styles.detailsLabel}>Component Stack:</p>
                                            <pre style={styles.detailsPre}>{this.state.errorInfo.componentStack}</pre>
                                        </>
                                    )}
                                </div>
                            </details>
                        )}

                        <p style={styles.hint}>
                            Si el problema persiste, contacta soporte indicando el código de error.
                        </p>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

const styles = {
    container: {
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #f0f4ff 0%, #e8f0fe 100%)',
        padding: 24,
        fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
    },
    card: {
        background: '#fff',
        borderRadius: 16,
        padding: '40px 32px',
        maxWidth: 520,
        width: '100%',
        textAlign: 'center',
        boxShadow: '0 10px 40px rgba(0,0,0,0.12)',
    },
    iconWrapper: {
        width: 72,
        height: 72,
        borderRadius: '50%',
        background: '#fee2e2',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 20px',
    },
    icon: {
        width: 40,
        height: 40,
    },
    title: {
        fontSize: 24,
        fontWeight: 700,
        color: '#1a1a2e',
        margin: '0 0 12px',
    },
    subtitle: {
        fontSize: 15,
        color: '#555',
        lineHeight: 1.5,
        margin: '0 0 24px',
    },
    errorIdBox: {
        background: '#f3f4f6',
        borderRadius: 8,
        padding: '12px 16px',
        marginBottom: 24,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
    },
    errorIdLabel: {
        fontSize: 11,
        color: '#6b7280',
        fontWeight: 500,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },
    errorIdCode: {
        fontSize: 14,
        color: '#1f2937',
        fontFamily: 'monospace',
        fontWeight: 600,
    },
    buttons: {
        display: 'flex',
        gap: 12,
        justifyContent: 'center',
        flexWrap: 'wrap',
        marginBottom: 20,
    },
    btnPrimary: {
        padding: '10px 24px',
        borderRadius: 8,
        border: 'none',
        background: '#2563eb',
        color: '#fff',
        fontSize: 14,
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'background 0.2s',
    },
    btnSecondary: {
        padding: '10px 24px',
        borderRadius: 8,
        border: '1px solid #d1d5db',
        background: '#fff',
        color: '#374151',
        fontSize: 14,
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'background 0.2s',
    },
    details: {
        textAlign: 'left',
        background: '#f9fafb',
        borderRadius: 8,
        padding: 16,
        marginBottom: 16,
        fontSize: 12,
    },
    detailsSummary: {
        cursor: 'pointer',
        fontWeight: 600,
        color: '#374151',
        userSelect: 'none',
    },
    detailsContent: {
        marginTop: 12,
        paddingTop: 12,
        borderTop: '1px solid #e5e7eb',
    },
    detailsLabel: {
        fontWeight: 600,
        color: '#6b7280',
        fontSize: 11,
        marginTop: 8,
        marginBottom: 4,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },
    detailsPre: {
        background: '#fff',
        padding: 8,
        borderRadius: 4,
        fontSize: 11,
        overflow: 'auto',
        maxHeight: 150,
        border: '1px solid #e5e7eb',
        margin: 0,
    },
    hint: {
        fontSize: 12,
        color: '#9ca3af',
        margin: 0,
    },
};

export default ErrorBoundary;
